/**
 * CenterZone.tsx — Router entre Dashboard, Department, Discussion, LiveChat, Canvas
 * InputBar fixe en bas (meme niveau que "Ma Productivite" dans sidebar)
 * V2: push_content panel, CREDO phase indicator, split-screen mode
 * Sprint B — CarlOS Noyau Omnipresent (D-081)
 */

import { useState, useEffect, useRef } from "react";
import {
  X,
  FileText,
  BarChart3,
  ListChecks,
  Table2,
  Bot,
  Pin,
  Loader2,
  CheckCircle2,
  Mic,
  RotateCcw,
} from "lucide-react";
import { cn } from "../../../components/ui/utils";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "../../../components/ui/resizable";
import { useFrameMaster } from "../../context/FrameMasterContext";
import { useCanvasActions } from "../../context/CanvasActionContext";
import type { ActiveView } from "../../context/FrameMasterContext";
import { BOT_AVATAR } from "../../api/types";
import { DashboardView } from "./DashboardView";
import { CockpitView } from "./blueprint/BlueprintDepartement";
import { SanteGlobaleView } from "./SanteGlobaleView";
import { DepartmentTourDeControle } from "./DepartmentTourDeControle";
import { DiscussionView } from "./DiscussionView";
import { BranchPatternsDemo } from "./BranchPatternsDemo";
import { CahierSmartDemo } from "./CahierSmartDemo";
import { ScenarioHub } from "./ScenarioHub";
import { LiveChat } from "./LiveChat";
import { SmartCanvas } from "./SmartCanvas";
import { DepartmentDetailView } from "./DepartmentDetailView";
// InputBar moved to SidebarRight (Sprint Final V1)
import { Orbit9DetailView } from "./orbit9/Orbit9DetailView";
import { AgentSettingsView } from "./AgentSettingsView";
import { MonBureauView } from "./MonBureauView";
import { BoardRoomView } from "./BoardRoomView";
import { WarRoomView } from "./WarRoomView";
import { ThinkRoomView } from "./ThinkRoomView";
import { FocusModeLayout } from "./FocusModeLayout";
import { PageTypePage } from "./orbit9/PageTypePage";
import { BibleTechniquePage } from "./orbit9/BibleTechniquePage";
import { BibleGHMLPage } from "./orbit9/BibleGHMLPage";
import { MasterRoadmapPage } from "./orbit9/MasterRoadmapPage";
import { MasterStrategiePage } from "./orbit9/MasterStrategiePage";
import { MasterOrbit9Page } from "./orbit9/MasterOrbit9Page";
import { MasterCommunicationPage } from "./orbit9/MasterCommunicationPage";
import { MasterDettePage } from "./orbit9/MasterDettePage";
import { MasterRoutinePage } from "./orbit9/MasterRoutinePage";
import { MasterMinedorPage } from "./orbit9/MasterMinedorPage";
import { MasterTrainingPage } from "./orbit9/MasterTrainingPage";
import { MasterProfilsPage } from "./orbit9/MasterProfilsPage";
import { MasterParcoursPage } from "./orbit9/MasterParcoursPage";
import { MasterNavigationPage } from "./orbit9/MasterNavigationPage";
import { MasterAnglesMortsPage } from "./orbit9/MasterAnglesMortsPage";
import { MasterCapacitesPage } from "./orbit9/MasterCapacitesPage";
import { MasterInstanceFondsPage } from "./orbit9/MasterInstanceFondsPage";
import MasterDiagnosticsPage from "./orbit9/MasterDiagnosticsPage";
import MasterPlaybooksPage from "./orbit9/MasterPlaybooksPage";
import MasterBibliothequeExecPage from "./orbit9/MasterBibliothequeExecPage";
import MasterMarketing360Page from "./orbit9/MasterMarketing360Page";
import MasterGuidesLegauxPage from "./orbit9/MasterGuidesLegauxPage";
import MasterCortexRobotPage from "./orbit9/MasterCortexRobotPage";
import MasterHydroQuebecPage from "./orbit9/MasterHydroQuebecPage";
import { MasterFlowsPage } from "./orbit9/MasterFlowsPage";
import { MasterCartographieIndustriellePage } from "./orbit9/MasterCartographieIndustriellePage";
import { MasterOracle9ConceptPage } from "./orbit9/MasterOracle9Page";
import { MasterBibleVisuelleLivePage } from "./orbit9/MasterBibleVisuelleLivePage";
import { MasterBibleVisuelCiblePage } from "./orbit9/MasterBibleVisuelCiblePage";
import { MasterBibleOfficielle } from "./orbit9/MasterBibleOfficielle";
import { FlowUsineBleuePage } from "./orbit9/FlowUsineBleuePage";
import { AnimationShowcasePage } from "./orbit9/AnimationShowcasePage";
import { AgentGalleryPage } from "./orbit9/AgentGalleryPage";
import { AccueilHeroPage } from "./orbit9/AccueilHeroPage";
import { PlaybookUsineBleuePage } from "./orbit9/PlaybookUsineBleuePage";
// StrategiqueView archived → replaced by BlueprintView
import { BlueprintView } from "./BlueprintView";
import { AtelierHub } from "./atelier/AtelierHub";
import { MonReseauView } from "./MonReseauView";
import { StrategiqueReseauPage } from "./orbit9/StrategiqueReseauPage";
import { FESidebarDroitePage } from "./orbit9/FESidebarDroitePage";
import { FEMonReseauPage } from "./orbit9/FEMonReseauPage";
import { CarlosCodesView } from "./CarlosCodesView";
import { DiagnosticIAPage } from "./diagnostic/DiagnosticIAPage";
// DiagnosticHubPage absorbed into SanteGlobaleView (Sprint C Phase 4) — archived
import { ConferenceAIView } from "./ConferenceAIView";
import { SallesHubView } from "./SallesHubView";
import { EquipeHumaineView } from "./EquipeHumaineView";
import { ChatH2H } from "./ChatH2H";
import { StatusView } from "./StatusView";
import { OnboardingView } from "./OnboardingView";
import { AdminPanel } from "./admin/AdminPanel";
// DocForgeView archived — fusionné dans MonBureauView (DocForge sub-tab)
import { useFlowGPS } from "../../api/hooks";
import { BOT_NAME, BOT_ROLE, BOT_AVATAR, BOT_SUBTITLE } from "../../api/types";
import { TopBarContent } from "../TopBar";

// ═══════════════════════════════════════
// MonEquipeHub — Grille de selection des 12 bots AI
// ═══════════════════════════════════════

const ALL_BOT_CODES = [
  "CEOB", "CTOB", "CFOB", "CMOB", "CSOB", "COOB",
  "CPOB", "CHROB", "CINOB", "CROB", "CLOB", "CISOB",
] as const;

const BOT_COLORS: Record<string, string> = {
  CEOB: "border-blue-200 hover:border-blue-400 hover:bg-blue-50/50",
  CTOB: "border-violet-200 hover:border-violet-400 hover:bg-violet-50/50",
  CFOB: "border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50/50",
  CMOB: "border-pink-200 hover:border-pink-400 hover:bg-pink-50/50",
  CSOB: "border-red-200 hover:border-red-400 hover:bg-red-50/50",
  COOB: "border-orange-200 hover:border-orange-400 hover:bg-orange-50/50",
  CPOB: "border-slate-200 hover:border-slate-400 hover:bg-slate-50/50",
  CHROB: "border-teal-200 hover:border-teal-400 hover:bg-teal-50/50",
  CINOB: "border-rose-200 hover:border-rose-400 hover:bg-rose-50/50",
  CROB: "border-amber-200 hover:border-amber-400 hover:bg-amber-50/50",
  CLOB: "border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50/50",
  CISOB: "border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50/50",
};

function MonEquipeHub() {
  const { navigateToDepartment } = useFrameMaster();

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-lg font-bold text-gray-800 mb-1">Mon Equipe AI</h2>
        <p className="text-sm text-gray-500 mb-6">12 agents specialises — cliquez pour acceder au departement</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {ALL_BOT_CODES.map((code) => (
            <button
              key={code}
              onClick={() => navigateToDepartment(code)}
              className={cn(
                "flex flex-col items-center gap-3 p-5 rounded-xl border-2 bg-white transition-all cursor-pointer shadow-sm hover:shadow-md",
                BOT_COLORS[code] || "border-gray-200 hover:border-gray-400"
              )}
            >
              <img
                src={BOT_AVATAR[code]}
                alt={BOT_NAME[code]}
                className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow"
              />
              <div className="text-center">
                <div className="text-sm font-semibold text-gray-800">{BOT_NAME[code]}</div>
                <div className="text-[9px] font-medium text-gray-500">{BOT_ROLE[code]}</div>
                <div className="text-[9px] text-gray-400 mt-0.5">{BOT_SUBTITLE[code]}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Couleur identitaire par bot — bande fine en haut du canevas */
/** Couleurs harmonisées avec les gradients headers (from-X-600) */
const BOT_BAND_COLORS: Record<string, string> = {
  CEOB: "bg-blue-600",
  CTOB: "bg-violet-600",
  CFOB: "bg-emerald-600",
  CMOB: "bg-pink-600",
  CSOB: "bg-red-600",
  COOB: "bg-orange-600",
  CPOB: "bg-slate-600",
  CHROB: "bg-teal-600",
  CINOB: "bg-rose-600",
  CROB: "bg-amber-600",
  CLOB: "bg-indigo-600",
  CISOB: "bg-zinc-600",
};

/** Labels CREDO par couche */
const CREDO_LABELS: Record<string, Record<string, string>> = {
  bouche:  { C: "Connecter",  R: "Rechercher", E: "Exposer",    D: "Demontrer",  O: "Obtenir" },
  cerveau: { C: "Clarifier",  R: "Reflechir",  E: "Elaborer",   D: "Decider",    O: "Operer" },
  coeur:   { C: "Comprendre", R: "Resonner",   E: "Elever",     D: "Debloquer",  O: "Ouvrir" },
};

const CREDO_COLORS: Record<string, string> = {
  C: "bg-blue-500",
  R: "bg-violet-500",
  E: "bg-amber-500",
  D: "bg-emerald-500",
  O: "bg-green-600",
};

/** Icone par type de contenu pousse */
const CONTENT_TYPE_ICONS: Record<string, React.ElementType> = {
  recommendations: ListChecks,
  tableau: Table2,
  metriques: BarChart3,
  plan_action: FileText,
  vocal: Mic,
  video: Bot,
};

/** FlowProgressBar — barre de progression pour sections ACTION */
function FlowProgressBar({ sectionKey }: { sectionKey: string }) {
  const { steps, currentStepIndex, isAction, completed, reset } = useFlowGPS(sectionKey);
  if (!isAction || steps.length === 0) return null;

  return (
    <div className="bg-white border-b px-4 py-2 shrink-0 flex items-center gap-2">
      {steps.map((step, i) => {
        const isDone = i < currentStepIndex || completed;
        const isCurrent = i === currentStepIndex && !completed;
        return (
          <div key={i} className="flex items-center gap-1.5">
            {i > 0 && (
              <div className={cn("w-6 h-0.5 rounded-full", isDone ? "bg-green-300" : "bg-gray-200")} />
            )}
            <span className={cn(
              "text-[9px] px-2.5 py-1 rounded-full font-medium whitespace-nowrap transition-all",
              isDone && "bg-green-100 text-green-700",
              isCurrent && "bg-blue-100 text-blue-700 ring-1 ring-blue-300",
              !isDone && !isCurrent && "bg-gray-50 text-gray-400"
            )}>
              {step}
            </span>
          </div>
        );
      })}
      {currentStepIndex > 0 && !completed && (
        <button
          onClick={reset}
          className="ml-auto text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded hover:bg-gray-100 transition-colors"
          title="Recommencer"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
      {completed && (
        <span className="ml-auto flex items-center gap-1 text-[9px] text-green-600 font-medium">
          <CheckCircle2 className="h-3.5 w-3.5" /> Termine
        </span>
      )}
    </div>
  );
}

export function CenterZone() {
  const { activeView, activeBotCode, activeEntrepriseSection, activeEquipeSection, setActiveView, navigateToDepartment, setActiveBotCode } = useFrameMaster();
  const {
    navigateAction, clearNavigateAction, consumeNext,
    activeWidget, activeAnnotation,
    pushedContent, credoPhases,
    executeAction,
    focusData, clearFocusMode,
    dismissWidget, dismissAnnotation,
    dismissPushedContent,
    dismissExecuteAction,
  } = useCanvasActions();
  const botBand = BOT_BAND_COLORS[activeBotCode];
  // liveChatMode removed — chat is in sidebar now
  const [pinnedContent, setPinnedContent] = useState(false);

  // --- Consommer les canvas actions navigate ---
  // Utilise navigateAction (state dedie) au lieu de lastAction
  // pour survivre au batching React quand plusieurs actions arrivent ensemble
  // GUARD: Si on est dans live-chat, ne PAS auto-naviguer ailleurs (fix S65 — Carl 12h01)
  useEffect(() => {
    if (!navigateAction || !navigateAction.view) return;
    // Chat is now permanently visible — allow all navigations
    const params = navigateAction.params as Record<string, unknown> | undefined;
    const botCode = params?.bot as string | undefined;
    if (botCode && (navigateAction.view === "department" || navigateAction.view === "detail")) {
      navigateToDepartment(botCode, navigateAction.view as ActiveView);
    } else {
      setActiveView(navigateAction.view as ActiveView);
    }
    clearNavigateAction();
    consumeNext();
  }, [navigateAction, activeView, setActiveView, navigateToDepartment, clearNavigateAction, consumeNext]);

  // Sync activeBotCode quand Focus Mode est dispatché avec un bot spécifique (fix bot-switch bug)
  useEffect(() => {
    if (focusData?.bot) {
      setActiveBotCode(focusData.bot);
    }
  }, [focusData, setActiveBotCode]);

  // Quitter le Focus Mode automatiquement quand on navigue via sidebar
  useEffect(() => {
    if (focusData) clearFocusMode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // --- Vision lunettes → auto-navigate CarlosCodesView quand code task detectee ---
  const lastDevTaskRef = useRef<string>("");
  useEffect(() => {
    const API_KEY = import.meta.env.VITE_API_KEY || "";
    const checkDevLive = async () => {
      try {
        const res = await fetch("/api/v1/dev/live", { headers: { "X-API-Key": API_KEY } });
        if (!res.ok) return;
        const data = await res.json();
        if (data.code_task_id && data.code_task_id !== lastDevTaskRef.current) {
          lastDevTaskRef.current = data.code_task_id;
          localStorage.setItem("carlos-codes-active-task", data.code_task_id);
          localStorage.setItem("carlos-codes-active-desc", data.stage || "");
          setActiveView("carlos-codes");
        }
      } catch { /* noop */ }
    };
    const interval = setInterval(checkDevLive, 5000);
    return () => clearInterval(interval);
  }, [setActiveView]);

  const handleStartChat = (_mode: string) => {
    // Chat is always visible in split — navigate to dashboard
    setActiveView("dashboard");
  };

  // Determiner si on montre l'indicateur CREDO (quand pas en phase default C/C/C)
  const showCredoIndicator = credoPhases.bouche !== "C" || credoPhases.cerveau !== "C" || credoPhases.coeur !== "C";

  const renderMainView = () => (
    <>
      {activeView === "dashboard" && <DashboardView />}
      {activeView === "cockpit" && <CockpitView />}
      {activeView === "health" && <SanteGlobaleView />}
      {activeView === "department" && <DepartmentTourDeControle />}
      {activeView === "discussion" && <DiscussionView />}
      {activeView === "branches" && <BranchPatternsDemo />}
      {activeView === "cahier" && <CahierSmartDemo />}
      {activeView === "scenarios" && <ScenarioHub />}
      {activeView === "ateliers" && <AtelierHub />}
      {activeView === "detail" && <DepartmentDetailView />}
      {activeView === "orbit9-detail" && <Orbit9DetailView />}
      {activeView === "agent-settings" && <AgentSettingsView />}
      {activeView === "espace-bureau" && <MonBureauView />}
      {activeView === "blueprint" && <BlueprintView />}
      {activeView === "board-room" && <BoardRoomView />}
      {activeView === "war-room" && <WarRoomView />}
      {activeView === "think-room" && <ThinkRoomView />}
      {activeView === "mes-chantiers" && <BlueprintView />}
      {activeView === "bible-visuelle" && <PageTypePage />}
      {activeView === "bible-technique" && <BibleTechniquePage />}
      {activeView === "bible-ghml" && <BibleGHMLPage />}
      {activeView === "master-roadmap" && <MasterRoadmapPage />}
      {activeView === "master-strategie" && <MasterStrategiePage />}
      {activeView === "master-orbit9" && <MasterOrbit9Page />}
      {activeView === "master-communication" && <MasterCommunicationPage />}
      {activeView === "master-dette" && <MasterDettePage />}
      {activeView === "master-routine" && <MasterRoutinePage />}
      {activeView === "master-minedor" && <MasterMinedorPage />}
      {activeView === "master-training" && <MasterTrainingPage />}
      {activeView === "master-profils" && <MasterProfilsPage />}
      {activeView === "master-parcours" && <MasterParcoursPage />}
      {activeView === "master-navigation" && <MasterNavigationPage />}
      {activeView === "master-angles-morts" && <MasterAnglesMortsPage />}
      {activeView === "master-capacites" && <MasterCapacitesPage />}
      {activeView === "master-instance-fonds" && <MasterInstanceFondsPage />}
      {activeView === "master-diagnostics" && <MasterDiagnosticsPage />}
      {activeView === "master-playbooks" && <MasterPlaybooksPage />}
      {activeView === "master-bibliotheque-exec" && <MasterBibliothequeExecPage />}
      {activeView === "master-marketing-360" && <MasterMarketing360Page />}
      {activeView === "master-guides-legaux" && <MasterGuidesLegauxPage />}
      {activeView === "master-cortex-robot" && <MasterCortexRobotPage />}
      {activeView === "master-hydro-quebec" && <MasterHydroQuebecPage />}
      {activeView === "master-flows" && <MasterFlowsPage />}
      {activeView === "master-cartographie" && <MasterCartographieIndustriellePage />}
      {activeView === "master-oracle9" && <MasterOracle9ConceptPage />}
      {activeView === "master-bible-live" && <MasterBibleVisuelleLivePage />}
      {activeView === "bible-visuelle-cible" && <MasterBibleVisuelCiblePage />}
      {activeView === "bible-officielle" && <MasterBibleOfficielle />}
      {activeView === "flow-usine-bleue" && <FlowUsineBleuePage />}
      {activeView === "animation-showcase" && <AnimationShowcasePage />}
      {activeView === "agent-gallery" && <AgentGalleryPage />}
      {activeView === "accueil-hero" && <AccueilHeroPage />}
      {activeView === "playbook-usine-bleue" && <PlaybookUsineBleuePage />}
      {activeView === "strategique-reseau" && <StrategiqueReseauPage />}
      {activeView === "fe-sidebar-droite" && <FESidebarDroitePage />}
      {activeView === "fe-mon-reseau" && <FEMonReseauPage />}
      {activeView === "mon-reseau" && <MonReseauView />}
      {activeView === "carlos-codes" && <CarlosCodesView />}
      {activeView === "diagnostic-ia" && <DiagnosticIAPage />}
      {/* diagnostic-hub absorbed into health view (Sprint C Phase 4) */}
      {activeView === "meeting-room" && <ConferenceAIView />}
      {activeView === "conference-ai" && <ConferenceAIView />}
      {activeView === "salles-hub" && <SallesHubView />}
      {activeView === "mon-entreprise" && <DepartmentTourDeControle />}
      {activeView === "mon-equipe" && <MonEquipeHub />}
      {activeView === "chat-h2h" && <ChatH2H />}
      {activeView === "status" && <StatusView />}
      {activeView === "onboarding" && <OnboardingView />}
      {activeView === "admin" && <AdminPanel mode="god" />}
      {activeView === "reglages" && <AdminPanel mode="instance" />}
      {/* DocForge fusionné dans MonBureauView — plus de vue séparée */}
      {activeView === "canvas" && (
        <SmartCanvas
          onStartChat={handleStartChat}
          onOpenScenarios={() => setActiveView("scenarios")}
        />
      )}
    </>
  );

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden relative">
      {/* Bandes couleur identitaire du bot actif — haut + gauche + droite du canevas */}
      {botBand && <div className={cn("h-1 shrink-0", botBand)} />}
      {botBand && <div className={cn("absolute left-0 top-0 bottom-0 w-1 z-10 pointer-events-none", botBand)} />}
      {botBand && <div className={cn("absolute right-0 top-0 bottom-0 w-1 z-10 pointer-events-none", botBand)} />}

      {/* Flow GPS bar — progression par etapes pour sections ACTION */}
      <FlowProgressBar sectionKey={activeView} />

      {/* Phase indicator — interne, sans labels visibles */}
      {showCredoIndicator && (
        <div className="bg-white border-b px-3 py-1 shrink-0 flex items-center gap-3">
          <Bot className="h-3.5 w-3.5 text-gray-400" />
        </div>
      )}

      {/* Vue principale — Split permanent: LiveChat gauche + Contenu droite */}
      <div className="flex-1 overflow-hidden flex">
        <ResizablePanelGroup
          direction="horizontal"
          autoSaveId="center-split-v1"
          className="h-full"
        >
          {/* Panel gauche — LiveChat permanent */}
          <ResizablePanel defaultSize={40} minSize={25} maxSize={60}>
            <div className="h-full overflow-hidden">
              <LiveChat splitMode splitTitle={
                focusData
                  ? (focusData.elementType === "blueprint_section" ? "Blueprint" :
                     focusData.elementType === "document_editor" ? "Document" :
                     focusData.elementType === "tim_code" ? "Code" :
                     focusData.elementType === "diagnostic_flow" ? "Diagnostic" :
                     "Atelier")
                  : undefined
              } />
            </div>
          </ResizablePanel>

          <ResizableHandle className="w-1 bg-gray-200 hover:bg-blue-400 transition-colors cursor-col-resize" />

          {/* Panel droite — TopBar nav + Contenu (FocusMode OU vue normale) */}
          <ResizablePanel defaultSize={60} minSize={30}>
            <div className="h-full flex flex-col overflow-hidden">
              <TopBarContent />
              <div className="flex-1 overflow-hidden">
                {focusData ? (
                  <FocusModeLayout focusData={focusData} onClose={clearFocusMode} />
                ) : (
                  renderMainView()
                )}
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Overlay: Push Content Panel — DÉSACTIVÉ (Carl: pas de popups sauf urgences) */}
      {/* Sera réactivé uniquement pour messages urgents à ne pas manquer */}
      {false && pushedContent && !focusData && (
        <div className={cn(
          "absolute top-12 right-3 z-50 w-80 max-h-[60vh] animate-in slide-in-from-right-4 duration-300",
          pinnedContent ? "opacity-100" : "opacity-95 hover:opacity-100"
        )}>
          <div className="bg-white border border-blue-200 rounded-xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-500 px-3 py-2 flex items-center gap-2">
              <img
                src={BOT_AVATAR[pushedContent.bot] || BOT_AVATAR["CEOB"]}
                alt={pushedContent.bot}
                className="w-5 h-5 rounded-full ring-1 ring-white/30"
              />
              <span className="text-xs font-bold text-white flex-1 truncate">{pushedContent.titre}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPinnedContent(!pinnedContent)}
                  className={cn(
                    "p-0.5 rounded transition-colors cursor-pointer",
                    pinnedContent ? "text-white bg-white/20" : "text-white/50 hover:text-white"
                  )}
                  title={pinnedContent ? "Desepingler" : "Epingler"}
                >
                  <Pin className="h-3 w-3" />
                </button>
                <button
                  onClick={() => { dismissPushedContent(); setPinnedContent(false); }}
                  className="text-white/50 hover:text-white cursor-pointer p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
            {/* Content type badges */}
            <div className="flex gap-1 px-3 pt-2">
              {pushedContent.contentTypes.map((ct) => {
                const CIcon = CONTENT_TYPE_ICONS[ct] || FileText;
                return (
                  <span key={ct} className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                    <CIcon className="h-2.5 w-2.5" />
                    {ct}
                  </span>
                );
              })}
            </div>
            {/* Content preview */}
            <div className="px-3 py-2 max-h-[40vh] overflow-auto">
              <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                {pushedContent.contenu}
              </div>
              {pushedContent.fullLength > 500 && (
                <p className="text-[10px] text-gray-400 mt-2 text-center">
                  ... {pushedContent.fullLength - 500} caracteres de plus dans le chat
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay: Execute Action — DESACTIVE (Carl: pas de pop-ups) */}
      {false && executeAction && !focusData && (
        <div className="absolute bottom-16 right-4 z-50 animate-in slide-in-from-right-4 duration-300">
          <div className="bg-gradient-to-r from-green-600 to-emerald-500 rounded-xl shadow-xl px-4 py-3 max-w-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <Loader2 className="h-4 w-4 text-white animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-white">CarlOS execute</p>
                <p className="text-[10px] text-white/80 mt-0.5">
                  {executeAction.message || "Action en cours..."}
                </p>
              </div>
              <button
                onClick={dismissExecuteAction}
                className="text-white/50 hover:text-white cursor-pointer p-1"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="mt-2 flex gap-2">
              <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white/60 rounded-full animate-pulse" style={{ width: "60%" }} />
              </div>
              <CheckCircle2 className="h-3.5 w-3.5 text-white/40" />
            </div>
          </div>
        </div>
      )}

      {/* Overlay: Widget contextuel CarlOS — DESACTIVE (Carl: pas de pop-ups) */}
      {false && activeWidget && !focusData && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-white border border-blue-200 rounded-xl shadow-lg px-4 py-3 max-w-md">
            <div className="flex items-start gap-2">
              <span className="text-blue-500 text-sm mt-0.5">
                {(activeWidget.data as Record<string, unknown>)?.action_type === "document" ? "📄" :
                 (activeWidget.data as Record<string, unknown>)?.action_type === "analyse" ? "🔍" : "💡"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-700">CarlOS</p>
                <p className="text-xs text-gray-600 mt-0.5">{activeWidget.message}</p>
              </div>
              <button
                onClick={dismissWidget}
                className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay: Annotation — DESACTIVE (Carl: pas de pop-ups) */}
      {false && activeAnnotation && !focusData && (
        <div className="absolute top-14 right-4 z-40 animate-in slide-in-from-right-4 duration-300">
          <div className={cn(
            "border rounded-lg shadow-md px-3 py-2 max-w-xs",
            (activeAnnotation.data as Record<string, unknown>)?.coaching_type === "celebration"
              ? "bg-green-50 border-green-300"
              : (activeAnnotation.data as Record<string, unknown>)?.coaching_type === "coaching"
              ? "bg-blue-50 border-blue-300"
              : "bg-amber-50 border-amber-300"
          )}>
            <div className="flex items-start gap-2">
              <span className="text-xs mt-0.5">
                {(activeAnnotation.data as Record<string, unknown>)?.coaching_type === "celebration" ? "🎉" :
                 (activeAnnotation.data as Record<string, unknown>)?.coaching_type === "coaching" ? "💙" : "✨"}
              </span>
              <p className={cn(
                "text-xs",
                (activeAnnotation.data as Record<string, unknown>)?.coaching_type === "celebration"
                  ? "text-green-800"
                  : (activeAnnotation.data as Record<string, unknown>)?.coaching_type === "coaching"
                  ? "text-blue-800"
                  : "text-amber-800"
              )}>
                {activeAnnotation.message}
              </p>
              <button
                onClick={dismissAnnotation}
                className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer shrink-0"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
